export const TERMS_LAST_UPDATED = "October 19, 2026";

export const termsContent = {
  en: {
    tagline: "Website and SMS terms",
    title: "Terms and Conditions",
    intro:
      "These Terms and Conditions (“Terms”) govern your use of the Drive Now Autohaus website, online inventory, forms, and related services, including the employee admin portal and our SMS / text messaging program. By using this website, submitting a form, creating or activating an account, or opting in to texts, you agree to these Terms.",
    dealerBlock: [
      "Drive Now Autohaus",
      "5401 W Cermak Rd, Cicero, IL 60804",
      "Phone: (708) 222-6100",
    ],
    sections: [
      {
        title: "1. Agreement to these Terms",
        paragraphs: [
          "Please read these Terms carefully. If you do not agree, do not use this website or opt in to our text messages. We may update these Terms from time to time. The “Last updated” date at the top will change when we do. Continued use of the site after an update means you accept the revised Terms.",
          "Our Privacy Policy explains how we collect and use personal information. It is available on the Privacy Policy page of this website and is incorporated by reference.",
        ],
      },
      {
        title: "2. Who we are",
        paragraphs: [
          "Drive Now Autohaus is a used-vehicle dealership located at 5401 W Cermak Rd, Cicero, IL 60804. Phone: (708) 222-6100. Hours: Monday through Saturday 10:00am–7:00pm; Sunday closed.",
          "“You” means a website visitor, customer, or (where applicable) a dealership employee using the admin portal.",
        ],
      },
      {
        title: "3. Eligibility",
        paragraphs: [
          "You must be at least 18 years old to use this website, submit forms, request financing, or opt in to SMS messages. By using the site or opting in to texts, you represent that you are 18 or older.",
        ],
      },
      {
        title: "4. License to use the website",
        paragraphs: [
          "We grant you a limited, non-exclusive, non-transferable license to access and use this website for lawful personal or internal business purposes related to shopping for, inquiring about, or servicing vehicles, or (for invited staff) performing job duties in the admin portal.",
          "You may not copy, scrape, crawl, reverse engineer, or commercially exploit the site, listings, photos, or data except as allowed by law or with our written permission.",
        ],
      },
      {
        title: "5. Inventory, pricing, and vehicle condition",
        paragraphs: [
          "Online listings, prices, photos, videos, descriptions, and availability can change without notice. A vehicle may be sold or reserved before you arrive. Taxes, title, registration, and fees are extra unless we say otherwise in writing.",
          "The vehicle on our lot is the vehicle offered for sale. Please inspect it in person, or have a mechanic you choose inspect it, before you buy. We do not guarantee that every listing is error-free.",
        ],
      },
      {
        title: "6. AI-generated or edited listing photos and videos",
        paragraphs: [
          "We may use artificial intelligence tools to generate, enhance, or edit photographs and videos of vehicles in inventory. Those images and videos are marketing and listing aids. We do not use AI to hide damage, change equipment, alter mileage, or otherwise deceive buyers about a vehicle’s condition, features, or history.",
          "If listing media looks inconsistent with the actual vehicle, contact us and we will correct the listing.",
        ],
      },
      {
        title: "7. Financing and pre-qualification",
        paragraphs: [
          "Loan calculators, pre-qualification tools, and financing pages provide estimates only. They are not a commitment to lend or a guarantee of any rate, term, or approval. Creditors make their own credit decisions. Full credit applications may be submitted through third-party lenders or Dealertrack / Cox Automotive tools and are subject to those companies’ terms.",
        ],
      },
      {
        title: "8. Trade-in estimates",
        paragraphs: [
          "Online or form-based trade-in values are estimates, not binding appraisals. Final trade-in value depends on an in-person inspection, condition, market, payoff, and other factors.",
        ],
      },
      {
        title: "9. Service appointments",
        paragraphs: [
          "Service scheduling requests are not confirmed until the dealership accepts them. We may reschedule or decline work. Service quotes are estimates unless we agree otherwise in writing.",
        ],
      },
      {
        title: "10. Employee admin accounts",
        paragraphs: [
          "Admin portal accounts are invite-only for Drive Now Autohaus employees and authorized staff. You must keep your password confidential, use only your own account, and tell us if you suspect unauthorized access. We may suspend or close an account at any time.",
          "Portal use is for dealership work only. Do not share customer information except as needed to do your job and as allowed by our Privacy Policy and the law.",
        ],
      },
      {
        title: "11. SMS / text messaging program (Drive Now Autohaus)",
        paragraphs: [
          "Program name: Drive Now Autohaus employee SMS notifications.",
          "If you opt in, Drive Now Autohaus may send recurring SMS (text) messages to the mobile number you provide. Messages may include employee account, lead, appointment, deal, inventory, and operations alerts. This program is for dealership employees who opt in on the account-activation web form (the page opened from the “Set Password & Activate” email) or later on the Admin Profile page.",
          "Message frequency varies. Message and data rates may apply. If you have questions about your text or data plan, contact your wireless provider. Supported carriers include major U.S. wireless carriers. Carriers are not liable for delayed or undelivered messages.",
          "Consent to receive texts is not a condition of employment or of using this website or the admin portal. You can activate your account and use the portal without opting in to SMS.",
          "How to opt in: on the activation web form (or Admin Profile), enter your mobile number and check the separate, unchecked SMS consent box. Checking the Terms and Privacy box does not, by itself, opt you in to texts.",
          "Customer-care texts: If you check the consent box on a form (including the contact form, vehicle inquiry, service request, trade-in, or financing application), you consent to receive customer-care text messages and/or phone calls from or on behalf of Drive Now Autohaus or our employees at the number you provided. Messages may include follow-up, appointment confirmations and reminders, and responses to your questions. Message frequency varies. Message and data rates may apply. Consent is not a condition of purchase.",
        ],
        bullets: [
          "STOP: You can cancel the SMS service at any time. Reply STOP to the number from which you received messages. After you send STOP, we will send a confirmation text. After that confirmation, you will no longer receive SMS messages from this program. To join again, opt in the same way you did the first time.",
          "HELP: If you need help with the messaging program, reply HELP, call (708) 222-6100, email us, or visit Drive Now Autohaus at 5401 W Cermak Rd, Cicero, IL 60804 during business hours.",
          "We do not sell, rent, or share mobile phone numbers with third parties or affiliates for marketing or promotional purposes. We may use vendors (such as our texting provider) solely to deliver our messages.",
          "Privacy: see our Privacy Policy for how we collect, use, and protect information, including mobile numbers.",
        ],
      },
      {
        title: "12. Intellectual property",
        paragraphs: [
          "The website, logos, design, text, software, and original photos/videos are owned by Drive Now Autohaus or our licensors. You may not use our marks without permission. Vehicle manufacturer marks belong to their owners.",
        ],
      },
      {
        title: "13. Prohibited uses",
        paragraphs: [
          "You may not use the site to break the law; harass others; submit false information; upload malware; attempt to gain unauthorized access; interfere with the site; or collect personal information of others except as allowed for your job in the admin portal.",
        ],
      },
      {
        title: "14. Third-party services",
        paragraphs: [
          "The site may link to lenders, manufacturers, Google, YouTube, Dealertrack / Cox Automotive, Cloudinary, messaging providers, and other third parties. Those services have their own terms and privacy policies. We are not responsible for their content or practices.",
        ],
      },
      {
        title: "15. Disclaimer of warranties",
        paragraphs: [
          "THE WEBSITE AND ITS CONTENT ARE PROVIDED “AS IS” AND “AS AVAILABLE.” TO THE FULLEST EXTENT PERMITTED BY LAW, WE DISCLAIM ALL WARRANTIES, EXPRESS OR IMPLIED, INCLUDING MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, AND NON-INFRINGEMENT. WE DO NOT WARRANT THAT THE SITE WILL BE UNINTERRUPTED, ERROR-FREE, OR FREE OF HARMFUL COMPONENTS.",
        ],
      },
      {
        title: "16. Limitation of liability",
        paragraphs: [
          "TO THE FULLEST EXTENT PERMITTED BY LAW, DRIVE NOW AUTOHAUS AND ITS OWNERS, EMPLOYEES, AND AGENTS ARE NOT LIABLE FOR INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, OR FOR LOST PROFITS, DATA, OR GOODWILL, ARISING FROM YOUR USE OF THE SITE, LISTINGS, FORMS, OR SMS PROGRAM. OUR TOTAL LIABILITY FOR CLAIMS ARISING FROM THESE TERMS OR THE SITE IS LIMITED TO THE GREATER OF ONE HUNDRED U.S. DOLLARS ($100) OR THE AMOUNT YOU PAID US (IF ANY) FOR THE SPECIFIC SERVICE GIVING RISE TO THE CLAIM IN THE THREE MONTHS BEFORE THE CLAIM.",
          "Some jurisdictions do not allow certain limitations. In those places, our liability is limited to the maximum extent permitted by law. Nothing in these Terms limits liability for fraud or for death or personal injury caused by our negligence where such a limit is not allowed.",
        ],
      },
      {
        title: "17. Indemnification",
        paragraphs: [
          "You agree to defend, indemnify, and hold harmless Drive Now Autohaus and its owners, employees, and agents from claims, damages, and expenses (including reasonable attorneys’ fees) arising from your misuse of the site, your violation of these Terms, or your violation of any law or third-party right.",
        ],
      },
      {
        title: "18. Governing law",
        paragraphs: [
          "These Terms are governed by the laws of the State of Illinois, without regard to conflict-of-law rules. You agree that courts located in Cook County, Illinois, have exclusive jurisdiction over disputes arising from these Terms or the website, except that we may seek injunctive relief in any court of competent jurisdiction.",
        ],
      },
      {
        title: "19. Changes; general",
        paragraphs: [
          "If a court finds any part of these Terms unenforceable, the rest remains in effect. Our failure to enforce a provision is not a waiver. These Terms are the entire agreement between you and us regarding the website, and they supersede prior website terms. They do not change a signed vehicle purchase contract, financing contract, or employment agreement, which control if there is a conflict about that transaction or employment.",
        ],
      },
      {
        title: "20. Contact",
        paragraphs: [
          "Questions about these Terms, the website, or the SMS program:",
        ],
        bullets: [
          "Drive Now Autohaus, 5401 W Cermak Rd, Cicero, IL 60804",
          "Phone: (708) 222-6100",
          "In person during business hours: Monday–Saturday 10:00am–7:00pm; Sunday closed",
          "SMS: reply STOP to opt out or HELP for help",
        ],
      },
    ],
  },
  es: {
    tagline: "Términos del sitio y SMS",
    title: "Términos y Condiciones",
    intro:
      "Estos Términos y Condiciones (“Términos”) rigen el uso del sitio web de Drive Now Autohaus, el inventario en línea, los formularios y servicios relacionados, incluido el portal de administración para empleados y nuestro programa de mensajes SMS. Al usar este sitio, enviar un formulario, crear o activar una cuenta, o aceptar textos, usted acepta estos Términos.",
    dealerBlock: [
      "Drive Now Autohaus",
      "5401 W Cermak Rd, Cicero, IL 60804",
      "Teléfono: (708) 222-6100",
    ],
    sections: [
      {
        title: "1. Aceptación de estos Términos",
        paragraphs: [
          "Lea estos Términos con cuidado. Si no está de acuerdo, no use este sitio ni opte por nuestros mensajes de texto. Podemos actualizar estos Términos. La fecha de “Última actualización” cambiará cuando lo hagamos. El uso continuado del sitio implica la aceptación de la versión revisada.",
          "Nuestra Política de Privacidad explica cómo recopilamos y usamos la información personal. Está en la página de Política de Privacidad de este sitio y se incorpora por referencia.",
        ],
      },
      {
        title: "2. Quiénes somos",
        paragraphs: [
          "Drive Now Autohaus es un concesionario de vehículos usados en 5401 W Cermak Rd, Cicero, IL 60804. Teléfono: (708) 222-6100. Horario: lunes a sábado 10:00am–7:00pm; domingo cerrado.",
        ],
      },
      {
        title: "3. Elegibilidad",
        paragraphs: [
          "Debe tener al menos 18 años para usar este sitio, enviar formularios, solicitar financiamiento u optar por SMS. Al usar el sitio o aceptar textos, declara que tiene 18 años o más.",
        ],
      },
      {
        title: "4. Licencia de uso del sitio",
        paragraphs: [
          "Le otorgamos una licencia limitada, no exclusiva e intransferible para acceder y usar este sitio con fines lícitos personales o internos relacionados con la compra, consulta o servicio de vehículos, o (para personal invitado) el desempeño de sus funciones en el portal de administración.",
          "No puede copiar, extraer, rastrear ni explotar comercialmente el sitio, los anuncios, las fotos o los datos, salvo lo que permita la ley o nuestra autorización por escrito.",
        ],
      },
      {
        title: "5. Inventario, precios y condición del vehículo",
        paragraphs: [
          "Los anuncios, precios, fotos, videos, descripciones y disponibilidad pueden cambiar sin aviso. Un vehículo puede venderse o reservarse antes de que usted llegue. Impuestos, título, registro y cargos son adicionales salvo que indiquemos lo contrario por escrito.",
          "El vehículo en nuestro lote es el vehículo que se ofrece a la venta. Inspecciónelo en persona, o haga que un mecánico de su elección lo inspeccione, antes de comprar.",
        ],
      },
      {
        title: "6. Fotos y videos de anuncios generados o editados con IA",
        paragraphs: [
          "Podemos usar herramientas de inteligencia artificial para generar, mejorar o editar fotografías y videos de vehículos del inventario. Esos medios son ayudas de mercadeo y de listado. No usamos IA para ocultar daños, cambiar el equipamiento, alterar el millaje ni para engañar a los compradores.",
          "Si los medios del anuncio no coinciden con el vehículo real, contáctenos y corregiremos el listado.",
        ],
      },
      {
        title: "7. Financiamiento y precalificación",
        paragraphs: [
          "Las calculadoras, herramientas de precalificación y páginas de financiamiento son solo estimaciones. No son un compromiso de crédito ni una garantía de tasa, plazo o aprobación. Los acreedores toman sus propias decisiones. Las solicitudes de crédito completas pueden enviarse a través de prestamistas o Dealertrack / Cox Automotive y se rigen por sus propios términos.",
        ],
      },
      {
        title: "8. Estimaciones de recibo de vehículo",
        paragraphs: [
          "Los valores de recibo en línea o en formularios son estimaciones, no tasaciones vinculantes. El valor final depende de la inspección en persona, la condición, el mercado, el saldo pendiente y otros factores.",
        ],
      },
      {
        title: "9. Citas de servicio",
        paragraphs: [
          "Las solicitudes de cita de servicio no están confirmadas hasta que el concesionario las acepte. Podemos reprogramar o rechazar el trabajo. Las cotizaciones de servicio son estimaciones salvo acuerdo escrito en contrario.",
        ],
      },
      {
        title: "10. Cuentas de administración de empleados",
        paragraphs: [
          "Las cuentas del portal son solo por invitación para empleados y personal autorizado de Drive Now Autohaus. Debe mantener su contraseña en confidencialidad, usar solo su cuenta e informarnos si sospecha acceso no autorizado. Podemos suspender o cerrar una cuenta en cualquier momento.",
          "El uso del portal es solo para el trabajo del concesionario. No comparta información de clientes salvo lo necesario para su trabajo y lo permitido por la Política de Privacidad y la ley.",
        ],
      },
      {
        title: "11. Programa de SMS / mensajes de texto (Drive Now Autohaus)",
        paragraphs: [
          "Nombre del programa: notificaciones SMS para empleados de Drive Now Autohaus.",
          "Si usted opta por participar, Drive Now Autohaus puede enviar mensajes SMS recurrentes al número móvil que proporcione. Los mensajes pueden incluir alertas de cuenta de empleado, prospectos, citas, tratos, inventario y operaciones. Este programa es para empleados del concesionario que optan en el formulario web de activación de cuenta (la página que se abre con el botón “Set Password & Activate” del correo) o más tarde en el Perfil de administración.",
          "La frecuencia de mensajes varía. Pueden aplicar tarifas de mensajes y datos. Si tiene preguntas sobre su plan, contacte a su operador. Los operadores principales de EE. UU. son compatibles. Los operadores no son responsables de mensajes retrasados o no entregados.",
          "El consentimiento para recibir textos no es condición de empleo ni del uso de este sitio o del portal. Puede activar su cuenta y usar el portal sin optar por SMS.",
          "Cómo optar: en el formulario web de activación (o en el Perfil), ingrese su número móvil y marque la casilla separada de consentimiento SMS, que no viene preseleccionada. Aceptar los Términos y la Privacidad no lo inscribe, por sí solo, a los textos.",
          "Textos de atención al cliente: Si marca la casilla de consentimiento en un formulario (incluido el de contacto, consulta de vehículo, solicitud de servicio, tasación o financiamiento), acepta recibir mensajes de texto y/o llamadas de atención al cliente de Drive Now Autohaus o nuestros empleados al número que indicó. Los mensajes pueden incluir seguimiento, confirmaciones y recordatorios de citas, y respuestas a sus preguntas. La frecuencia varía. Pueden aplicar tarifas de mensajes y datos. El consentimiento no es condición de compra.",
        ],
        bullets: [
          "STOP: Puede cancelar el servicio SMS en cualquier momento. Responda STOP al número del que recibió los mensajes. Después de enviar STOP, le enviaremos un texto de confirmación. Después de esa confirmación, ya no recibirá SMS de este programa. Para volver a unirse, opte de la misma forma que la primera vez.",
          "HELP: Si necesita ayuda con el programa de mensajes, responda HELP, llame al (708) 222-6100, envíenos un correo o visítenos en 5401 W Cermak Rd, Cicero, IL 60804 en horario de atención.",
          "No vendemos, alquilamos ni compartimos números móviles con terceros o afiliados para mercadeo o promoción. Podemos usar proveedores (por ejemplo, nuestro proveedor de textos) solo para entregar nuestros mensajes.",
          "Privacidad: consulte nuestra Política de Privacidad.",
        ],
      },
      {
        title: "12. Propiedad intelectual",
        paragraphs: [
          "El sitio, logotipos, diseño, texto, software y fotos/videos originales pertenecen a Drive Now Autohaus o a nuestros licenciantes. No use nuestras marcas sin permiso.",
        ],
      },
      {
        title: "13. Usos prohibidos",
        paragraphs: [
          "No puede usar el sitio para infringir la ley; acosar a otros; enviar información falsa; cargar malware; intentar acceso no autorizado; interferir con el sitio; ni recopilar información personal de terceros salvo lo permitido para su trabajo en el portal.",
        ],
      },
      {
        title: "14. Servicios de terceros",
        paragraphs: [
          "El sitio puede enlazar a prestamistas, fabricantes, Google, YouTube, Dealertrack / Cox Automotive, Cloudinary, proveedores de mensajería y otros. Esos servicios tienen sus propios términos y políticas. No somos responsables de su contenido ni prácticas.",
        ],
      },
      {
        title: "15. Exención de garantías",
        paragraphs: [
          "EL SITIO Y SU CONTENIDO SE OFRECEN “TAL CUAL” Y “SEGÚN DISPONIBILIDAD.” EN LA MÁXIMA MEDIDA PERMITIDA POR LA LEY, RENUNCIAMOS A TODAS LAS GARANTÍAS, EXPRESAS O IMPLÍCITAS, INCLUIDAS COMERCIABILIDAD, IDONEIDAD PARA UN FIN PARTICULAR Y NO INFRACCIÓN.",
        ],
      },
      {
        title: "16. Limitación de responsabilidad",
        paragraphs: [
          "EN LA MÁXIMA MEDIDA PERMITIDA POR LA LEY, DRIVE NOW AUTOHAUS Y SUS PROPIETARIOS, EMPLEADOS Y AGENTES NO SON RESPONSABLES DE DAÑOS INDIRECTOS, INCIDENTALES, ESPECIALES, CONSECUENTES O PUNITIVOS, NI DE LUCRO CESANTE, DATOS O CLIENTELA, DERIVADOS DEL USO DEL SITIO, LOS ANUNCIOS, LOS FORMULARIOS O EL PROGRAMA SMS. NUESTRA RESPONSABILIDAD TOTAL SE LIMITA AL MAYOR ENTRE CIEN DÓLARES DE EE. UU. ($100) O LO QUE NOS HAYA PAGADO (SI APLICA) POR EL SERVICIO ESPECÍFICO EN LOS TRES MESES ANTERIORES AL RECLAMO.",
        ],
      },
      {
        title: "17. Indemnización",
        paragraphs: [
          "Usted acepta defender, indemnizar y eximir de responsabilidad a Drive Now Autohaus y a sus propietarios, empleados y agentes frente a reclamos, daños y gastos (incluidos honorarios razonables de abogados) derivados del mal uso del sitio, de su incumplimiento de estos Términos o de la violación de cualquier ley o derecho de terceros.",
        ],
      },
      {
        title: "18. Ley aplicable",
        paragraphs: [
          "Estos Términos se rigen por las leyes del Estado de Illinois, sin considerar normas de conflicto de leyes. Usted acepta la jurisdicción exclusiva de los tribunales del Condado de Cook, Illinois, salvo que podamos solicitar medidas cautelares en cualquier tribunal competente.",
        ],
      },
      {
        title: "19. Cambios; general",
        paragraphs: [
          "Si un tribunal declara inaplicable alguna parte, el resto permanece vigente. El no ejercer un derecho no es una renuncia. Estos Términos son el acuerdo completo respecto del sitio. No modifican un contrato de compraventa, financiamiento o empleo firmado, que prevalece en caso de conflicto sobre esa transacción o relación laboral.",
        ],
      },
      {
        title: "20. Contacto",
        paragraphs: [
          "Drive Now Autohaus, 5401 W Cermak Rd, Cicero, IL 60804. Teléfono: (708) 222-6100. Para SMS: responda STOP para cancelar o HELP para ayuda. Horario: lunes a sábado 10:00am–7:00pm; domingo cerrado.",
        ],
      },
    ],
  },
};
